/**
 * Analytics Service — Founder Analytics
 *
 * Tracks events to the `analytics_events` table for both
 * anonymous (public mentor browsing) and authenticated (SNBT) users.
 *
 * All tracking is fire-and-forget — errors are silently caught
 * to never break UX.
 */

import { supabase } from '../supabaseClient';
import { getSessionId, getAuthUserId } from '../utils/sessionTracker';

interface TrackEventOptions {
    targetId?: string;
    metadata?: Record<string, unknown>;
}

/**
 * Core event tracking function.
 * Inserts a row into analytics_events with session_id and/or user_id.
 */
async function trackEvent(eventType: string, opts: TrackEventOptions = {}): Promise<void> {
    try {
        const sessionId = getSessionId();
        const userId = await getAuthUserId();

        let username: string | undefined = undefined;
        if (userId) {
            const { data: { session } } = await supabase.auth.getSession();
            username = session?.user?.user_metadata?.full_name || session?.user?.email || undefined;
        }

        const enrichedMetadata = {
            ...(opts.metadata || {}),
            ...(username ? { username } : {})
        };

        await supabase.from('analytics_events').insert({
            event_type: eventType,
            user_id: userId,
            session_id: sessionId,
            target_id: opts.targetId ?? null,
            metadata: Object.keys(enrichedMetadata).length > 0 ? enrichedMetadata : null,
        });
    } catch (err) {
        // Silent failure — analytics must never break UX
        console.error('[Analytics] trackEvent error:', err);
    }
}

// =========================
// PUBLIC LAYER EVENTS
// =========================

/**
 * Track when a user views a mentor's detail profile.
 * Called when the MentorDetailModal opens.
 */
export function trackMentorProfileViewed(mentorName: string): void {
    trackEvent('mentor_profile_viewed', {
        targetId: mentorName,
        metadata: { mentor_name: mentorName },
    });
}

/**
 * Track when a user clicks to contact a mentor (WhatsApp).
 * Called when handleContactClick fires in App.tsx.
 */
export function trackMentorContactClick(mentorName: string): void {
    trackEvent('mentor_contact_click', {
        targetId: mentorName,
        metadata: { mentor_name: mentorName },
    });
}

/**
 * Track when a user actually clicks "Hubungi via WhatsApp" in the SOP modal.
 * Called when proceedToWhatsapp fires in App.tsx.
 */
export function trackMentorActualContact(mentorName: string): void {
    trackEvent('mentor_actual_contact', {
        targetId: mentorName,
        metadata: { mentor_name: mentorName },
    });
}

// =========================
// PRIVATE LAYER EVENTS (SNBT)
// =========================

/**
 * Track when a user starts a tryout.
 * Called after a new tryout_attempt is created.
 */
export function trackTryoutStarted(tryoutId: string, tryoutName?: string): void {
    trackEvent('tryout_started', {
        targetId: tryoutId,
        metadata: { tryout_id: tryoutId, tryout_name: tryoutName },
    });
}

/**
 * Track when a user completes a tryout.
 * Includes score metadata for founder insights.
 */
export function trackTryoutCompleted(
    tryoutId: string,
    score: number,
    tryoutName?: string
): void {
    trackEvent('tryout_completed', {
        targetId: tryoutId,
        metadata: {
            tryout_id: tryoutId,
            tryout_name: tryoutName,
            total_score: score,
        },
    });
}
